import IconArrowUp from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconArrowUp.js";
import React, { useState, ReactNode, useEffect, useId } from "react";
import styled from "styled-components";

interface AccordionProps {
  children: ReactNode;
  trigger: ReactNode;
  triggerPosition?: "top" | "bottom";
  withChevronRight?: boolean;
  withChevronLeft?: boolean;
  isOpenByDefault?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const Wrapper = styled.div`
  width: 100%;
`;

const AccordionContent = styled.div<{ $isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.$isOpen ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s ease-out;
  overflow: hidden;

  ${(props) =>
    props.$isOpen &&
    `
    animation: enableOverflow 0s 0.3s forwards;
  `}

  @keyframes enableOverflow {
    to {
      overflow: visible;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    overflow: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  }
`;

const AccordionInner = styled.div`
  min-height: 0;
`;

const TriggerWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  width: 100%;
  background-color: transparent;
  border: none;
  padding: 0;
`;

const StyledIconArrowUp = styled(IconArrowUp)<{ $isOpen: boolean }>`
  transform: ${(props) => (props.$isOpen ? "rotate(0deg)" : "rotate(180deg)")};
  transition: transform 0.3s ease-out;
  color: ${(props) => props.theme.kallisto.color.icon.static.iconSecondary};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Accordion: React.FC<AccordionProps> = ({
  children,
  trigger,
  triggerPosition = "bottom",
  withChevronRight = false,
  withChevronLeft = false,
  isOpenByDefault = false,
  onToggle,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  const [hasBeenOpened, setHasBeenOpened] = useState(isOpenByDefault);
  const id = useId();
  const triggerId = `accordion-trigger-${id}`;
  const contentId = `accordion-content-${id}`;

  const toggleAccordion = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
    if (!hasBeenOpened) {
      setHasBeenOpened(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleAccordion();
    }
  };

  useEffect(() => {
    if (isOpenByDefault) {
      setHasBeenOpened(true);
    }
  }, [isOpenByDefault]);

  const triggerElement = (
    <TriggerWrapper
      as="button"
      id={triggerId}
      onClick={toggleAccordion}
      onKeyDown={handleKeyDown}
      aria-expanded={isOpen}
      aria-controls={contentId}
      data-testid="regionCountryCardButton"
    >
      {withChevronLeft && (
        <StyledIconArrowUp $isOpen={isOpen} size={14} aria-hidden="true" />
      )}
      {trigger}
      {withChevronRight && (
        <StyledIconArrowUp $isOpen={isOpen} size={14} aria-hidden="true" />
      )}
    </TriggerWrapper>
  );

  return (
    <Wrapper role="region" aria-label="Accordion">
      {triggerPosition === "top" && triggerElement}
      <AccordionContent
        $isOpen={isOpen}
        id={contentId}
        role="region"
        aria-labelledby={triggerId}
        hidden={!isOpen}
      >
        <AccordionInner>{hasBeenOpened && children}</AccordionInner>
      </AccordionContent>
      {triggerPosition === "bottom" && triggerElement}
    </Wrapper>
  );
};

export default Accordion;
